import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { WEB_LOGIN_PATH } from "../../../routes/authPaths"
import '../../../assets/scss/reset-password.scss'
import EyeIcon from "../../../assets/images/icons/eye.svg"
import EyeSlashIcon from "../../../assets/images/icons/eye-slash.svg"
import { supabaseClient } from "../../../utils/supabase"
import { useTheme } from "../../../contexts/ThemeContext"
import getThemeColors from "../../../utils/getColorTheme"

export default function ResetPassword() {
  const [params] = useSearchParams()
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [message, setMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [messageType, setMessageType] = useState("")
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const { addTheme } = useTheme()
  const { theme, background } = getThemeColors(addTheme)

  // Get email from query parameter or use hash parameter for Supabase SAAS mode
  const email = params.get("email")
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001"

  useEffect(() => {
    if (!email) {
      setMessage("Email not provided. Please request a new link.")
      setMessageType("error")
    }
  }, [email])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!password || !confirmPassword) {
      setMessage("Please fill in all fields.")
      setMessageType("error")
      return
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.")
      setMessageType("error")
      return
    }

    setIsLoading(true)
    setMessage("")

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok) {
        setMessage("Password reset successful. Redirecting to login...")
        setMessageType("success")
        setTimeout(() => {
          navigate(WEB_LOGIN_PATH)
        }, 2000)
      } else {
        setMessage(data.error || "Error resetting password.")
        setMessageType("error")
      }
    } catch (err) {
      console.error("Reset password error:", err)
      setMessage(err.message || "Something went wrong.")
      setMessageType("error")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="reset-password-wrapper" style={{ background: background }}>
      <div className="reset-password-modal">
        <form onSubmit={handleSubmit} className="reset-password-form">
          <h2 className="reset-password-subtitle" style={{ color: theme }}>Enter your new password</h2>
          <div className="form-group">
            <div className="group">
              <label htmlFor="setPassword" className="form-label" style={{ color: theme }}>
                New password
              </label>
              <div className="input-password-wrapper" style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="setPassword"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Type your password"
                  required
                  disabled={(!email) || isLoading}
                  style={{ paddingRight: "40px", background: "transparent", color: theme }}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={showPassword ? EyeSlashIcon : EyeIcon}
                    alt={showPassword ? "Hide password" : "Show password"}
                    style={{ width: "20px", height: "20px", filter: addTheme === "dark" ? "brightness(0) invert(1)" : "none" }}
                  />
                </span>
              </div>
            </div>

            <div className="group">
              <label htmlFor="setPasswordConfirm" className="form-label" style={{ color: theme }}>
                Confirm new password
              </label>
              <div className="input-password-wrapper" style={{ position: "relative" }}>
                <input
                  type={showPasswordConfirm ? "text" : "password"}
                  id="setPasswordConfirm"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  disabled={(!email) || isLoading}
                  style={{ paddingRight: "40px", background: "transparent", color: theme }}
                />
                <span
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={showPasswordConfirm ? EyeSlashIcon : EyeIcon}
                    alt={showPasswordConfirm ? "Hide password" : "Show password"}
                    style={{ width: "20px", height: "20px", filter: addTheme === "dark" ? "brightness(0) invert(1)" : "none" }}
                  />
                </span>
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            className="reset-button" 
            disabled={isLoading || !email}
            aria-label="Reset your password"
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>

          {message && (
            <p 
              className={`message ${messageType}`} 
              aria-live="polite"
              style={{ color: theme }}
            >
              {message}
            </p>
          )}

          <button 
            type="button" 
            className="back-link" 
            onClick={() => navigate(WEB_LOGIN_PATH)}
            aria-label="Return to login page"
            style={{ color: theme }}
          >
            Back to Login
          </button>
        </form>
      </div>
    </div>
  )
}
